import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MODAL_ACTION } from '../../../config/enums';
import useObjectElements from '../../../hooks/useObjectElements';
import Loader from '../../Loader';
import ModalView from '../../ModalView';
import useDeleteStandardizationProject from '../hooks/useDeleteStandardizationProject';
import useArchiveStandardizationProject from '../hooks/useArchiveStandardizationProject';
import useUnarchiveStandardizationProject from '../hooks/useUnarchiveStandardizationProject';
import useDuplicateStandardizationProject from '../hooks/useDuplicateStandardizationProject';
import useToggleStarStandardizationProject from '../hooks/useToggleStarStandardizationProject';
import {
  Wrapper,
  ProjectItemWrapper,
  ProjectWrapper,
  ProjectIcon,
  ProjectInfoWrapper,
  ProjectName,
  ProjectInfo,
  DeleteProjectBtnSmall,
  ArchiveProjectBtnSmall,
  ArchiveProjectBtn,
  UnarchiveProjectBtn,
  DuplicateProjectBtn,
  StarProjectBtn,
  NoProjectText,
  ToggleTrainingWrapper,
  ToggleTrainingBtn,
  ArchivedProjectsWrapper,
  ToggleArchivedProjectsWrapper,
  ArchivedProjectList,
} from './styles';

const ProjectsList = ({ projects = [], archivedProjects = [], isLoading, selectedProjectId, onClick = () => undefined, canCreateDelete = false, isCastAdmin = false }) => {
  const history = useHistory();
  const { deleteProject, isLoading: isDeletingProject } = useDeleteStandardizationProject();
  const { archiveProject, isLoading: isArchivingProject } = useArchiveStandardizationProject();
  const { unarchiveProject, isLoading: isUnarchivingProject } = useUnarchiveStandardizationProject();
  const { duplicateProject, isLoading: isDuplicatingProject } = useDuplicateStandardizationProject();
  const { toggleStarProject } = useToggleStarStandardizationProject();
  const { object: modalData, setObject: setModalData, setObjectValue: setModalProp } = useObjectElements({});
  const [training, setTraining] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const onDeleteClick = (event, id) => {
    event.stopPropagation();
    const deleteModal = {
      modalIsOpen: true,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'NO, do not delete!',
      rightBtnTitle: 'YES, delete project!',
      modalDescription: 'Are you sure you want to delete this standardization project? Warning: this action cannot be undone!',
      onCloseModal: onCloseModal,
      onBtnLeftClick: onCloseModal,
      onBtnRightClick: () => deleteProjectHandler(id),
    };

    setModalData(deleteModal);
  };
  const onArchiveClick = (event, id) => {
    event.stopPropagation();
    const archiveModal = {
      modalIsOpen: true,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'NO, do not archive!',
      rightBtnTitle: 'YES, archive project!',
      modalDescription: 'Are you sure you want to archive this standardization project?',
      onCloseModal: onCloseModal,
      onBtnLeftClick: onCloseModal,
      onBtnRightClick: () => archiveProjectHandler(id),
    };

    setModalData(archiveModal);
  };
  const onUnarchiveClick = (event, id) => {
    event.stopPropagation();
    const unarchiveModal = {
      modalIsOpen: true,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'NO, do not restore!',
      rightBtnTitle: 'YES, restore project!',
      modalDescription: 'Are you sure you want to restore this standardization project?',
      onCloseModal: onCloseModal,
      onBtnLeftClick: onCloseModal,
      onBtnRightClick: () => unarchiveProjectHandler(id),
    };

    setModalData(unarchiveModal);
  };

  const onDuplicateClick = (event, id, phase) => {
    event.stopPropagation();
    const deleteModal = {
      modalIsOpen: true,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'Cancel',
      rightBtnTitle: 'Duplicate',
      modalDescription: `Are you sure you want to create a duplicate? ${phase > 1 && '\nWarning! This is a phase '+phase+' project. You will not be able to delete the new project.'}`,
      onCloseModal: onCloseModal,
      onBtnLeftClick: onCloseModal,
      onBtnRightClick: () => duplicateProjectHandler(id),
    };

    setModalData(deleteModal);
  };

  const onStarClick = (event, id) => {
    event.stopPropagation();
    toggleStarProject(id);
  };

  const onCloseModal = () => setModalProp(MODAL_ACTION.IS_OPEN, false);

  const onSuccessfulDeletedProject = (deletedProjectId) => {
    onCloseModal();
    if (deletedProjectId === selectedProjectId) {
      history.push('/standardization');
    }
  };

  const onSuccessfulArchivedProject = (archivedProjectId) => {
    onCloseModal();
    if (archivedProjectId === selectedProjectId) {
      history.push('/standardization');
    }
  };

  const onSuccessfulUnarchivedProject = (unarchivedProjectId) => {
    onCloseModal();
    if (unarchivedProjectId === selectedProjectId) {
      history.push('/standardization');
    }
  };

  const onSuccessfulDuplicatedProject = (duplicatedProject) => {
    onCloseModal();
  };

  const deleteProjectHandler = (id) => {
    deleteProject(id, { onSuccess: onSuccessfulDeletedProject });
  };
  const archiveProjectHandler = (id) => {
    archiveProject(id, { onSuccess: onSuccessfulArchivedProject });
  };
  const unarchiveProjectHandler = (id) => {
    unarchiveProject(id, { onSuccess: onSuccessfulUnarchivedProject });
  };

  const duplicateProjectHandler = (id) => {
    duplicateProject(id, { onSuccess: onSuccessfulDuplicatedProject });
  };

  const toggleTrainingHandler = (event) => {
    setTraining(!training);
  };

  const toggleArchivedHandler = (event) => {
    setShowArchived(!showArchived);
  };

  const sortedProjects = useMemo(() => orderBy(projects, ['forTraining', 'starred', 'name'], ['asc', 'desc', 'asc']), [projects]);
  const sortedArchivedProjects = useMemo(() => orderBy(archivedProjects, ['name'], ['asc']), [archivedProjects]);

  if (isLoading) {
    return <Loader size={75} />;
  }
  return (
    <>
      <ToggleTrainingWrapper>
        <ToggleTrainingBtn onClick={toggleTrainingHandler}>{training ? 'Hide training assessments' : 'Show training assessments'}</ToggleTrainingBtn>
      </ToggleTrainingWrapper>
      <Wrapper>
        {projects.length > 0 ? (
          sortedProjects.map(({ id, name, layoutDiagramId, phase, project, nrOfModules, starred, forTraining }) => {
            if (!training && forTraining) return null;
            else return (
            <ProjectItemWrapper key={id}>
              <ProjectWrapper
                onClick={() => onClick({ id })}
                isSelected={selectedProjectId === id}
                training={forTraining}>
                <ProjectIcon />
                <ProjectInfoWrapper>
                  <ProjectName>{project}</ProjectName>
                  <ProjectInfo>
                    {name} | Phase {phase} | {nrOfModules} {nrOfModules === 1 ? 'module' : 'modules'}
                  </ProjectInfo>
                </ProjectInfoWrapper>
                <StarProjectBtn
                  isStarred={starred}
                  onClick={(event) => onStarClick(event, id)}
                />
                {canCreateDelete && !forTraining && (
                  phase <= 1 ? <><DeleteProjectBtnSmall
                    onClick={(event) => onDeleteClick(event, id)}
                    classNames='delete-project-btn'
                    hoverText='Delete'
                  /><ArchiveProjectBtnSmall
                    onClick={(event) => onArchiveClick(event, id)}
                    classNames='delete-project-btn'
                    hoverText='Archive'
                  /></> : <ArchiveProjectBtn
                    onClick={(event) => onArchiveClick(event, id)}
                    classNames="delete-project-btn"
                    hoverText='Archive'
                  />
                    )}
                {canCreateDelete && (
                  <div title="Duplicate"><DuplicateProjectBtn
                    onClick={(event) => onDuplicateClick(event, id, phase)}
                  /></div>
                    )}
              </ProjectWrapper>
            </ProjectItemWrapper>
          )
          })
        ) : (
          <NoProjectText>
            You do not have any <br /> assessments
          </NoProjectText>
        )}
        {isCastAdmin && <ArchivedProjectsWrapper>
          <ToggleArchivedProjectsWrapper>
            <ToggleTrainingBtn onClick={toggleArchivedHandler}>{showArchived ? 'Hide archived assessments' : 'Show archived assessments'}</ToggleTrainingBtn>
          </ToggleArchivedProjectsWrapper>
          {showArchived && <ArchivedProjectList>
            {sortedArchivedProjects.length > 0 ? (
              sortedArchivedProjects.map(({ id, name, phase, project, nrOfModules }) => {
                return (
                  <ProjectItemWrapper key={id}>
                    <ProjectWrapper>
                      <ProjectIcon />
                      <ProjectInfoWrapper>
                        <ProjectName>{project}</ProjectName>
                        <ProjectInfo>
                          {name} | Phase {phase} | {nrOfModules} {nrOfModules === 1 ? 'module' : 'modules'}
                        </ProjectInfo>
                      </ProjectInfoWrapper>
                      {canCreateDelete && (
                        <UnarchiveProjectBtn
                          onClick={(event) => onUnarchiveClick(event, id)}
                          classNames='delete-project-btn'
                        />
                      )}
                    </ProjectWrapper>
                  </ProjectItemWrapper>
                )
              })
              ) : (
              <NoProjectText>
                You do not have any <br /> archived assessments
              </NoProjectText>
            )}
              </ArchivedProjectList>}
        </ArchivedProjectsWrapper>}
      </Wrapper>
      <ModalView
        isOpen={modalData.modalIsOpen}
        title={modalData.modalTitle}
        description={modalData.modalDescription}
        onCloseBtn={modalData.onCloseModal}
        onBtnLeftClick={modalData.onBtnLeftClick}
        onBtnRightClick={modalData.onBtnRightClick}
        leftBtnTitle={modalData.leftBtnTitle}
        rightBtnTitle={modalData.rightBtnTitle}
        disableBtns={isDeletingProject}
      />
    </>
  );
};

export default ProjectsList;
